import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowRight,
  Play,
  Users,
  Clock,
  Star,
  CheckCircle,
  Phone,
  Mail,
  Calendar,
} from "lucide-react";
import { mixpanel } from "../../utils/mixpanel"; // Assuming mixpanel.init() is called elsewhere or here is fine.
import DashboardHeader from "../uncommon/native/DashboardHeader";

// --- Constants (Styles & Colors) ---
// Design system colors matching your app
const colors = {
  primary: "#00B7F4",
  primaryDark: "#0099CC",
  background: "#FFFFFF",
  cardBg: "#f9f9f9",
  lightBlueBg: "#F1FAFF",
  accentBg: "#EAF6FF",
  title: "#111",
  text: "#444",
  subtext: "#555",
  border: "#E5E5E5",
  success: "#22C55E",
  warning: "#F97316",
};

// Common styles matching your app
const styles = {
  container: {
    backgroundColor: colors.background,
    position: "relative",
    minHeight: "100vh",
  },
  card: {
    backgroundColor: colors.cardBg,
    borderRadius: "16px",
    padding: "24px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
  },
  cardPressed: {
    transform: "scale(0.98)",
    boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
  },
  title: {
    fontSize: "36px",
    fontWeight: "700",
    color: colors.title,
    fontFamily: "Urbanist, system-ui, sans-serif",
    lineHeight: "1.2",
    marginBottom: "24px",
  },
  subtitle: {
    fontSize: "20px",
    fontWeight: "400",
    color: colors.subtext,
    fontFamily: "Urbanist, system-ui, sans-serif",
    lineHeight: "1.5",
    marginBottom: "32px",
  },
  normalText: {
    fontSize: "16px",
    fontWeight: "400",
    color: colors.text,
    fontFamily: "Urbanist, system-ui, sans-serif",
    lineHeight: "1.5",
  },
  primaryButton: {
    backgroundColor: colors.primary,
    color: colors.background,
    padding: "16px 32px",
    borderRadius: "24px",
    fontSize: "16px",
    fontWeight: "600",
    fontFamily: "Urbanist, system-ui, sans-serif",
    border: "none",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    transition: "all 0.2s ease",
  },
  primaryButtonHover: {
    backgroundColor: colors.primaryDark,
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(0,183,244,0.3)",
  },
  secondaryButton: {
    backgroundColor: "transparent",
    color: colors.text,
    padding: "16px 32px",
    borderRadius: "24px",
    fontSize: "16px",
    fontWeight: "600",
    fontFamily: "Urbanist, system-ui, sans-serif",
    border: `1px solid ${colors.border}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
  },
  tourCard: {
    backgroundColor: colors.lightBlueBg,
    borderRadius: "16px",
    padding: "32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    textAlign: "center",
    marginBottom: "32px",
  },
  input: {
    width: "100%",
    padding: "16px",
    borderRadius: "12px",
    border: `1px solid ${colors.border}`,
    fontSize: "16px",
    fontFamily: "Urbanist, system-ui, sans-serif",
    backgroundColor: colors.background,
    transition: "border-color 0.2s ease",
  },
  inputFocus: {
    // This can be handled by global CSS or inline onFocus/onBlur
    borderColor: colors.primary,
    outline: "none",
  },
  label: {
    fontSize: "14px",
    fontWeight: "500",
    color: colors.title,
    fontFamily: "Urbanist, system-ui, sans-serif",
    marginBottom: "8px",
    display: "block",
  },
};

// --- Page Components (Defined Outside) ---

const QRLandingPageComponent = ({
  stats,
  isVideoPlaying,
  setIsVideoPlaying,
  trackButtonClick,
  trackFunnelStep,
  setCurrentPage,
  demoStartTime,
  getEventProperties,
  referrerCode,
  handleToggleHamburger,
  isMobileMenuOpen,
}) => (
  <div style={styles.container}>
    <DashboardHeader
      handleToggleHamburger={handleToggleHamburger}
      isMobileMenuOpen={isMobileMenuOpen}
      hideSignout={true}
    />
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "48px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            backgroundColor: colors.accentBg,
            color: colors.primary,
            padding: "8px 16px",
            borderRadius: "24px",
            fontSize: "14px",
            fontWeight: "500",
            marginBottom: "24px",
          }}
        >
          <Star style={{ width: "16px", height: "16px", marginRight: "8px" }} />
          Personal Invitation from Dr. Palladino
        </div>
        <h1 style={styles.title}>
          Transform Your Patient Consultations with AI
        </h1>
        <p style={{ ...styles.subtitle, maxWidth: "800px", margin: "0 auto" }}>
          Join an exclusive group of surgeons using ClinicOS to enhance patient
          education with AI-powered simulations and streamlined consultation
          workflows.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "24px",
          marginBottom: "48px",
        }}
      >
        <div
          style={{ ...styles.card, cursor: "pointer" }}
          onClick={() => {
            mixpanel.track(
              "FOMO Stat Clicked",
              getEventProperties({
                // Assuming mixpanel is globally available or passed
                stat_type: "surgeons_count",
                value: stats.surgeons,
              }),
            );
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: colors.primary,
              marginBottom: "8px",
            }}
          >
            {stats.surgeons}+
          </div>
          <div style={styles.normalText}>Consultations Documented with AI</div>
          <div
            style={{
              fontSize: "14px",
              color: colors.success,
              marginTop: "4px",
            }}
          >
            ↗ Growing Daily
          </div>
        </div>
        <div
          style={{ ...styles.card, cursor: "pointer" }}
          onClick={() => {
            mixpanel.track(
              "FOMO Stat Clicked",
              getEventProperties({
                stat_type: "time_saved",
                value: stats.timeSaved,
              }),
            );
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: colors.primary,
              marginBottom: "8px",
            }}
          >
            {stats.timeSaved} hrs
          </div>
          <div style={styles.normalText}>Saved Per Week</div>
          <div
            style={{
              fontSize: "14px",
              color: colors.success,
              marginTop: "4px",
            }}
          >
            On average per surgeon
          </div>
        </div>
        <div
          style={{ ...styles.card, cursor: "pointer" }}
          onClick={() => {
            mixpanel.track(
              "FOMO Stat Clicked",
              getEventProperties({
                stat_type: "limited_spots",
                value: "limited", // This seems like a fixed string, original had stats.procedures
              }),
            );
          }}
        >
          <div
            style={{
              fontSize: "32px",
              fontWeight: "700",
              color: colors.primary,
              marginBottom: "8px",
            }}
          >
            10000+
          </div>
          <div style={styles.normalText}>Before / Afters Created</div>
          <div
            style={{
              fontSize: "14px",
              color: colors.warning,
              marginTop: "4px",
            }}
          >
            Early Access Only
          </div>
        </div>
      </div>

      <div
        style={{
          ...styles.card,
          maxWidth: "900px",
          margin: "0 auto 48px",
          padding: "0",
          overflow: "hidden",
          backgroundColor: colors.background,
        }}
      >
        <div
          style={{
            backgroundColor: "#000",
            position: "relative",
            paddingBottom: "56.25%", // 16:9 aspect ratio
            width: "100%",
          }}
        >
          {!isVideoPlaying ? (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                background:
                  'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url("https://pub-0991182fdab143cf938953a8d6ab8813.r2.dev/video-assets/video-thumbnail.jpg") center/cover',
              }}
              onClick={() => {
                setIsVideoPlaying(true);
                trackButtonClick("Play Product Video", {
                  video_name: "ClinicOS Product Overview",
                  location: "landing_page_main",
                });
                mixpanel.track(
                  "Video Started",
                  getEventProperties({
                    // Direct mixpanel call
                    video_name: "ClinicOS Product Overview",
                    video_duration: "2:30",
                    page: "qr-landing", // currentPage can be passed to getEventProperties
                    referrer_code: referrerCode,
                  }),
                );
              }}
            >
              <button
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "rgba(0, 183, 244, 0.9)",
                  borderRadius: "50%",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: "0 4px 20px rgba(0, 183, 244, 0.4)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 30px rgba(0, 183, 244, 0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 20px rgba(0, 183, 244, 0.4)";
                }}
              >
                <Play
                  style={{
                    width: "36px",
                    height: "36px",
                    color: "white",
                    marginLeft: "4px",
                  }}
                />
              </button>
            </div>
          ) : (
            <iframe
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
              }}
              src="https://player.vimeo.com/video/YOUR_VIDEO_ID?autoplay=1" // Replace YOUR_VIDEO_ID
              allow="autoplay; fullscreen"
              allowFullScreen
              onLoad={() => {
                mixpanel.track(
                  "Video Player Loaded",
                  getEventProperties({
                    video_name: "ClinicOS Product Overview",
                  }),
                );
              }}
            ></iframe>
          )}
        </div>
        <div
          style={{
            padding: "24px",
            textAlign: "center",
            backgroundColor: colors.lightBlueBg,
          }}
        >
          <h3
            style={{
              fontSize: "20px",
              fontWeight: "600",
              color: colors.title,
              marginBottom: "8px",
            }}
          >
            See ClinicOS in Action
          </h3>
          <p
            style={{
              ...styles.normalText,
              fontSize: "16px",
              marginBottom: "16px",
            }}
          >
            Watch how Dr. Palladino transforms patient consultations with
            AI-powered simulations
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "24px",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Clock
                style={{ width: "16px", height: "16px", color: colors.primary }}
              />
              <span style={{ fontSize: "14px", color: colors.subtext }}>
                2:30 minutes
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Users
                style={{ width: "16px", height: "16px", color: colors.primary }}
              />
              <span style={{ fontSize: "14px", color: colors.subtext }}>
                Real Demo
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <Star
                style={{ width: "16px", height: "16px", color: colors.primary }}
              />
              <span style={{ fontSize: "14px", color: colors.subtext }}>
                Live simulation
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          ...styles.card,
          marginBottom: "48px",
          maxWidth: "900px",
          margin: "0 auto 48px",
        }}
      >
        <h3
          style={{
            fontSize: "20px",
            fontWeight: "600",
            textAlign: "center",
            marginBottom: "24px",
            color: colors.title,
          }}
        >
          What Colleagues Are Saying
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "24px",
          }}
        >
          <div
            style={{
              borderLeft: `4px solid ${colors.primary}`,
              paddingLeft: "16px",
              cursor: "pointer",
            }}
            onClick={() => {
              mixpanel.track(
                "Testimonial Clicked",
                getEventProperties({
                  doctor_name: "Dr. Larry Fan",
                  specialty: "Plastic Surgery",
                  testimonial_position: 1,
                }),
              );
            }}
          >
            <p style={{ ...styles.normalText, marginBottom: "8px" }}>
              "ClinicOS has revolutionized how I present options to patients.
              The AI simulations are incredibly realistic and FAST."
            </p>
            <p
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: colors.title,
              }}
            >
              Dr. Larry Fan, 77 Plastic Surgery
            </p>
          </div>
          <div
            style={{
              borderLeft: `4px solid ${colors.primary}`,
              paddingLeft: "16px",
              cursor: "pointer",
            }}
            onClick={() => {
              mixpanel.track(
                "Testimonial Clicked",
                getEventProperties({
                  doctor_name: "Dr. Humberto Palladino", // Updated name
                  specialty: "Reconstructive Surgery", // Assuming specialty based on original context
                  testimonial_position: 2,
                }),
              );
            }}
          >
            <p style={{ ...styles.normalText, marginBottom: "8px" }}>
              "This makes documentation so simple. Record or upload the audio
              and its processed and formatted perfectly! These guys need to
              charge more."
            </p>
            <p
              style={{
                fontSize: "14px",
                fontWeight: "500",
                color: colors.title,
              }}
            >
              Dr. Humberto Palladino, TopAesthetics
            </p>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => {
            trackButtonClick("See How It Works", {
              location: "hero_section",
              destination: "demo",
            });
            trackFunnelStep("view_demo");
            setCurrentPage("demo");
            if (!demoStartTime.current) {
              demoStartTime.current = Date.now();
            }
          }}
          style={styles.primaryButton}
          onMouseEnter={(e) => {
            Object.assign(e.target.style, styles.primaryButtonHover);
            mixpanel.track(
              "Button Hovered",
              getEventProperties({
                button_name: "See How It Works",
              }),
            );
          }}
          onMouseLeave={(e) =>
            Object.assign(e.target.style, styles.primaryButton)
          }
        >
          See How It Works
          <ArrowRight style={{ width: "20px", height: "20px" }} />
        </button>
        <div
          style={{ fontSize: "14px", color: colors.subtext, marginTop: "12px" }}
        >
          2-minute demo • No commitment required
        </div>
      </div>

      <div
        style={{
          ...styles.tourCard,
          backgroundColor: "#FEF3C7",
          border: "1px solid #F59E0B",
          padding: "16px",
          marginTop: "48px",
        }}
      >
        <p style={{ color: "#92400E", margin: 0 }}>
          <strong>Limited Time:</strong> Early access includes free setup and 3
          months of premium features
        </p>
      </div>
    </div>
  </div>
);

const DemoPageComponent = ({
  trackButtonClick,
  trackFunnelStep,
  setCurrentPage,
  signupStartTime,
  demoStartTime,
  getEventProperties,
  handleToggleHamburger,
  isMobileMenuOpen,
}) => (
  <div style={styles.container}>
    <DashboardHeader
      handleToggleHamburger={handleToggleHamburger}
      isMobileMenuOpen={isMobileMenuOpen}
      hideSignout={true}
    />
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <h1 style={{ ...styles.title, fontSize: "30px" }}>
          Experience ClinicOS in Action
        </h1>
        <p style={styles.normalText}>
          See how leading surgeons are transforming their patient consultations
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(350px, 1fr))",
          gap: "24px",
          marginBottom: "48px",
        }}
      >
        {/* AI Simulations Demo Card */}
        <div
          style={{ ...styles.card, overflow: "hidden", cursor: "pointer" }}
          onClick={() => {
            trackButtonClick("Demo Feature Card", {
              feature: "AI Simulations",
              action: "clicked",
            });
          }}
          onMouseEnter={() => {
            mixpanel.track(
              "Feature Card Hovered",
              getEventProperties({
                feature: "AI Simulations",
              }),
            );
          }}
        >
          <div
            style={{
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "16px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "4px",
              }}
            >
              AI-Powered Simulations
            </h3>
            <p style={{ ...styles.normalText, fontSize: "14px" }}>
              Generate realistic procedure outcomes instantly
            </p>
          </div>
          <div>
            <div
              style={{
                backgroundColor: colors.lightBlueBg,
                borderRadius: "12px",
                height: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <div style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: "64px",
                    height: "64px",
                    backgroundColor: colors.primary,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 8px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    trackButtonClick("Play Demo", {
                      demo_type: "AI Simulation",
                      location: "feature_card",
                    });
                    mixpanel.track(
                      "Demo Video Started",
                      getEventProperties({
                        video_name: "AI Simulation Demo",
                        feature: "AI Simulations",
                      }),
                    );
                  }}
                >
                  <Play
                    style={{ width: "32px", height: "32px", color: "white" }}
                  />
                </div>
                <p style={{ fontSize: "14px", color: colors.subtext }}>
                  Watch Simulation Generation
                </p>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "14px",
                color: colors.subtext,
              }}
            >
              <span>Before → AI Processing → Simulation</span>
              <span>~30s</span>
            </div>
          </div>
        </div>

        {/* Voice Notes Demo Card */}
        <div style={{ ...styles.card, overflow: "hidden" }}>
          <div
            style={{
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "16px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "4px",
              }}
            >
              Smart Voice Notes
            </h3>
            <p style={{ ...styles.normalText, fontSize: "14px" }}>
              Automatic transcription and AI summaries
            </p>
          </div>
          <div>
            <div
              style={{
                backgroundColor: "#FEE2E2",
                border: "1px solid #FCA5A5",
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    backgroundColor: "#EF4444",
                    borderRadius: "50%",
                    marginRight: "8px",
                    animation: "pulse 2s infinite",
                  }}
                ></div>
                <span style={{ fontSize: "14px", fontWeight: "500" }}>
                  Recording... 0:45
                </span>
              </div>
              <p style={{ fontSize: "14px", color: "#7F1D1D" }}>
                "Patient shows excellent healing progress. Discussed scar
                management..."
              </p>
            </div>
            <div style={{ fontSize: "12px", color: colors.subtext }}>
              ✓ Auto-transcribed ✓ AI Summary ✓ Attached to Record
            </div>
          </div>
        </div>

        {/* Similar Patients Gallery Card */}
        <div style={{ ...styles.card, overflow: "hidden" }}>
          <div
            style={{
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "16px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "4px",
              }}
            >
              Similar Patient Gallery
            </h3>
            <p style={{ ...styles.normalText, fontSize: "14px" }}>
              Show relevant before/after cases
            </p>
          </div>
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    backgroundColor: colors.lightBlueBg,
                    borderRadius: "8px",
                    aspectRatio: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span style={{ fontSize: "12px", color: colors.subtext }}>
                    Case {i}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "14px", color: colors.subtext }}>
              Auto-matched by procedure, age, and characteristics
            </div>
          </div>
        </div>

        {/* EHR Integration Card */}
        <div style={{ ...styles.card, overflow: "hidden" }}>
          <div
            style={{
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "16px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "4px",
              }}
            >
              Seamless EHR Integration
            </h3>
            <p style={{ ...styles.normalText, fontSize: "14px" }}>
              Works with Epic, Cerner, and more
            </p>
          </div>
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                backgroundColor: colors.lightBlueBg,
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <div>
                <div style={{ fontWeight: "500", color: colors.title }}>
                  Emma Thompson
                </div>
                <div style={{ fontSize: "14px", color: colors.subtext }}>
                  DOB: 03/15/1985
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: colors.success,
                  fontSize: "14px",
                }}
              >
                <CheckCircle
                  style={{ width: "16px", height: "16px", marginRight: "4px" }}
                />
                Synced
              </div>
            </div>
            <div style={{ fontSize: "12px", color: colors.subtext }}>
              Patient data, photos, and notes automatically synchronized
            </div>
          </div>
        </div>

        {/* Before/After Gallery Card */}
        <div
          style={{ ...styles.card, overflow: "hidden", cursor: "pointer" }}
          onClick={() => {
            trackButtonClick("Demo Feature Card", {
              feature: "Before/After Gallery",
              action: "clicked",
            });
          }}
          onMouseEnter={() => {
            mixpanel.track(
              "Feature Card Hovered",
              getEventProperties({
                feature: "Before/After Gallery",
              }),
            );
          }}
        >
          <div
            style={{
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "16px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "4px",
              }}
            >
              Stunning Before/After Images
            </h3>
            <p style={{ ...styles.normalText, fontSize: "14px" }}>
              AI-powered image enhancement and templates
            </p>
          </div>
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  backgroundColor: "#F3F4F6",
                  borderRadius: "8px",
                  aspectRatio: "3/4",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    left: "8px",
                    backgroundColor: "rgba(0,0,0,0.7)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  BEFORE
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    right: "8px",
                    backgroundColor: colors.primary,
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "10px",
                  }}
                >
                  Enhanced
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "#ECFDF5",
                  borderRadius: "8px",
                  aspectRatio: "3/4",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "8px",
                    left: "8px",
                    backgroundColor: "rgba(34,197,94,0.9)",
                    color: "white",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  AFTER
                </div>
                <div
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    right: "8px",
                    backgroundColor: colors.success,
                    color: "white",
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "10px",
                  }}
                >
                  Template
                </div>
              </div>
            </div>
            <div style={{ fontSize: "12px", color: colors.subtext }}>
              ✓ Auto-align ✓ Remove backgrounds ✓ Blur sensitive areas ✓ Custom
              templates
            </div>
          </div>
        </div>

        {/* Lead Generation Card */}
        <div
          style={{ ...styles.card, overflow: "hidden", cursor: "pointer" }}
          onClick={() => {
            trackButtonClick("Demo Feature Card", {
              feature: "Lead Generation",
              action: "clicked",
            });
          }}
          onMouseEnter={() => {
            mixpanel.track(
              "Feature Card Hovered",
              getEventProperties({
                feature: "Lead Generation",
              }),
            );
          }}
        >
          <div
            style={{
              borderBottom: `1px solid ${colors.border}`,
              paddingBottom: "16px",
              marginBottom: "16px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "4px",
              }}
            >
              Website Lead Generation
            </h3>
            <p style={{ ...styles.normalText, fontSize: "14px" }}>
              Interactive simulations capture leads
            </p>
          </div>
          <div>
            <div
              style={{
                backgroundColor: colors.lightBlueBg,
                borderRadius: "12px",
                padding: "16px",
                marginBottom: "16px",
                border: `1px solid ${colors.primary}20`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: colors.title,
                  }}
                >
                  Interactive Simulation
                </div>
                <div
                  style={{
                    backgroundColor: colors.primary,
                    color: "white",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                  }}
                >
                  Live
                </div>
              </div>
              <div
                style={{
                  backgroundColor: "white",
                  borderRadius: "8px",
                  padding: "12px",
                  fontSize: "12px",
                  color: colors.subtext,
                  marginBottom: "8px",
                }}
              >
                "See your potential results instantly"
              </div>
              <button
                style={{
                  backgroundColor: colors.primary,
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "12px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Get My Consultation →
              </button>
            </div>
            <div style={{ fontSize: "12px", color: colors.subtext }}>
              ✓ Website integration ✓ Lead tracking ✓ Analytics dashboard ✓
              Social media ready
            </div>
          </div>
        </div>
      </div>

      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => {
            trackButtonClick("Start Your Free Trial", {
              location: "demo_bottom",
              destination: "signup",
            });
            trackFunnelStep("start_signup");
            setCurrentPage("signup");
            if (!signupStartTime.current) {
              signupStartTime.current = Date.now();
              mixpanel.time_event("Signup Completed"); // Ensure this is correctly used if it's a timing start
            }
            if (demoStartTime.current) {
              const demoTime = Math.round(
                (Date.now() - demoStartTime.current) / 1000,
              );
              mixpanel.track(
                "Demo Completed",
                getEventProperties({
                  // Pass getEventProperties
                  time_spent_seconds: demoTime,
                  completed: true,
                  features_viewed: 4,
                }),
              );
            }
          }}
          style={styles.primaryButton}
          onMouseEnter={(e) =>
            Object.assign(e.target.style, styles.primaryButtonHover)
          }
          onMouseLeave={(e) =>
            Object.assign(e.target.style, styles.primaryButton)
          }
        >
          Start Your Free Trial
          <ArrowRight style={{ width: "20px", height: "20px" }} />
        </button>
        <p
          style={{ fontSize: "14px", color: colors.subtext, marginTop: "8px" }}
        >
          Includes personal onboarding with our team
        </p>
      </div>
    </div>
  </div>
);

const SignupPageComponent = ({
  signupStep,
  setSignupStep,
  formData,
  setFormData, // Or individual handlers like handleNameChange
  handleNameChange,
  handleNameFocus,
  handleNameBlur,
  handlePracticeChange,
  handlePracticeFocus,
  handlePracticeBlur,
  handleEmailChange,
  handleEmailFocus,
  handleEmailBlur,
  trackButtonClick,
  trackFunnelStep,
  trackFormField, // Pass the memoized version
  signupStartTime,
  getEventProperties,
  handleToggleHamburger,
  isMobileMenuOpen,
}) => (
  <div style={styles.container}>
    <DashboardHeader
      handleToggleHamburger={handleToggleHamburger}
      isMobileMenuOpen={isMobileMenuOpen}
      hideSignout={true}
    />
    <div style={{ maxWidth: "600px", margin: "0 auto", padding: "48px 24px" }}>
      <div style={{ marginBottom: "32px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: "500",
              color: colors.primary,
            }}
          >
            Step {signupStep} of 3
          </span>
          <span style={{ fontSize: "14px", color: colors.subtext }}>
            Referred by Dr. Palladino
          </span>
        </div>
        <div
          style={{
            width: "100%",
            backgroundColor: colors.border,
            borderRadius: "4px",
            height: "8px",
          }}
        >
          <div
            style={{
              backgroundColor: colors.primary,
              height: "8px",
              borderRadius: "4px",
              width: `${(signupStep / 3) * 100}%`,
              transition: "width 0.3s ease",
            }}
          ></div>
        </div>
      </div>

      {signupStep === 1 && (
        <div style={styles.card}>
          <h2
            style={{ ...styles.title, fontSize: "24px", marginBottom: "24px" }}
          >
            Tell us about your practice
          </h2>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "24px" }}
          >
            <div>
              <label htmlFor="name" style={styles.label}>
                Your Name
              </label>
              <input
                id="name"
                type="text"
                style={styles.input}
                placeholder="Dr. John Smith"
                value={formData.name}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary; // Direct style for focus
                  handleNameFocus();
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border; // Direct style for blur
                  handleNameBlur();
                }}
                onChange={handleNameChange}
              />
            </div>
            <div>
              <label htmlFor="practiceName" style={styles.label}>
                Practice Name
              </label>
              <input
                id="practiceName"
                type="text"
                style={styles.input}
                placeholder="Smith Aesthetic Surgery"
                value={formData.practice}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary;
                  handlePracticeFocus();
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  handlePracticeBlur();
                }}
                onChange={handlePracticeChange}
              />
            </div>
            <div>
              <label htmlFor="specialty" style={styles.label}>
                Specialty
              </label>
              <select
                id="specialty"
                style={styles.input}
                value={formData.specialty}
                onChange={(e) => {
                  setFormData({ ...formData, specialty: e.target.value });
                  trackFormField("specialty", "selected");
                  mixpanel.track(
                    "Specialty Selected",
                    getEventProperties({
                      specialty: e.target.value,
                    }),
                  );
                }}
              >
                <option>Plastic Surgery</option>
                <option>Reconstructive Surgery</option>
                <option>Cosmetic Surgery</option>
                <option>Derm</option>
                <option>Med Spa</option>
                <option>Weight Loss</option>
                <option>Wellness</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="email" style={styles.label}>
                Email Address
              </label>
              <input
                id="email"
                type="email"
                style={styles.input}
                placeholder="john@smithsurgery.com"
                value={formData.email}
                onFocus={(e) => {
                  e.target.style.borderColor = colors.primary;
                  handleEmailFocus();
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = colors.border;
                  handleEmailBlur();
                }}
                onChange={handleEmailChange}
              />
            </div>
          </div>
          <button
            onClick={() => {
              trackButtonClick("Continue", {
                signup_step: 1,
                form_data_complete: !!(formData.name && formData.email),
              });
              trackFunnelStep("complete_profile", {
                form_fields_filled: Object.keys(formData).filter(
                  (k) => formData[k],
                ).length,
              });
              setSignupStep(2);
            }}
            style={{
              ...styles.primaryButton,
              width: "100%",
              marginTop: "32px",
            }}
          >
            Continue
          </button>
        </div>
      )}

      {signupStep === 2 && (
        <div style={styles.card}>
          <div style={{ marginBottom: "32px" }}>
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "600",
                color: colors.title,
                marginBottom: "16px",
              }}
            >
              A message from Dr. Palladino
            </h3>
            <div
              style={{
                backgroundColor: "#111",
                borderRadius: "12px",
                height: "200px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <button
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  borderRadius: "50%",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={() => {
                  trackButtonClick("Play Video", {
                    video: "Dr. Palladino Welcome",
                    location: "signup_step_2",
                  });
                  mixpanel.track(
                    "Video Started",
                    getEventProperties({
                      video_name: "Dr. Palladino Welcome Message",
                      signup_step: 2,
                    }),
                  );
                }}
              >
                <Play
                  style={{
                    width: "32px",
                    height: "32px",
                    color: "white",
                    marginLeft: "4px",
                  }}
                />
              </button>
              <div
                style={{
                  position: "absolute",
                  bottom: "16px",
                  left: "16px",
                  color: "white",
                  fontSize: "14px",
                }}
              >
                "Welcome to the future of surgical consultations..."
              </div>
            </div>
          </div>
          <h2
            style={{ ...styles.title, fontSize: "24px", marginBottom: "24px" }}
          >
            EHR Integration Setup
          </h2>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {["Epic", "Cerner", "Allscripts", "MEDITECH", "Other"].map(
              (ehr) => (
                <label
                  key={ehr}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px",
                    border: `1px solid ${formData.ehr_system === ehr ? colors.primary : colors.border}`,
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    backgroundColor:
                      formData.ehr_system === ehr
                        ? colors.lightBlueBg
                        : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    name="ehr"
                    style={{ marginRight: "16px" }}
                    checked={formData.ehr_system === ehr}
                    onChange={() => {
                      setFormData({ ...formData, ehr_system: ehr });
                      trackFormField("ehr_system", "selected");
                      mixpanel.track(
                        "EHR System Selected",
                        getEventProperties({
                          ehr_system: ehr,
                          signup_step: 2,
                        }),
                      );
                    }}
                  />
                  <span style={{ fontWeight: "500" }}>{ehr}</span>
                </label>
              ),
            )}
          </div>
          <div style={{ display: "flex", gap: "16px", marginTop: "32px" }}>
            <button
              onClick={() => {
                trackButtonClick("Back", { from_step: 2, to_step: 1 });
                setSignupStep(1);
              }}
              style={{ ...styles.secondaryButton, flex: 1 }}
            >
              Back
            </button>
            <button
              onClick={() => {
                trackButtonClick("Continue", {
                  signup_step: 2,
                  ehr_selected: !!formData.ehr_system,
                });
                trackFunnelStep("select_ehr", {
                  ehr_system: formData.ehr_system || "none",
                });
                setSignupStep(3);
              }}
              style={{ ...styles.primaryButton, flex: 1 }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {signupStep === 3 && (
        <div style={{ ...styles.card, textAlign: "center" }}>
          {(() => {
            // IIFE for immediate tracking on render
            if (signupStartTime.current) {
              // Check if signup process actually started
              const signupTime = Math.round(
                (Date.now() - signupStartTime.current) / 1000,
              );
              mixpanel.track(
                "Signup Completed",
                getEventProperties({
                  time_spent_seconds: signupTime,
                  "User Email": formData.email,
                  name: formData.name,
                  practice: formData.practice,
                  specialty: formData.specialty,
                  ehr_system: formData.ehr_system,
                }),
              );
              trackFunnelStep("finish_signup", {
                total_time_seconds: signupTime,
              });
              signupStartTime.current = null; // Prevent re-tracking if component re-renders for other reasons
            }
            return null;
          })()}
          <div
            style={{
              width: "64px",
              height: "64px",
              backgroundColor: "#DCFCE7",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <CheckCircle
              style={{ width: "32px", height: "32px", color: colors.success }}
            />
          </div>
          <h2
            style={{ ...styles.title, fontSize: "24px", marginBottom: "16px" }}
          >
            Welcome to ClinicOS!
          </h2>
          <p style={{ ...styles.normalText, marginBottom: "32px" }}>
            Your account is being set up. Our team will contact you within 24
            hours to schedule your personalized onboarding session.
          </p>
          <div
            style={{
              ...styles.tourCard,
              textAlign: "left",
              marginBottom: "32px",
            }}
          >
            <h3
              style={{
                fontWeight: "600",
                color: colors.title,
                marginBottom: "16px",
              }}
            >
              What happens next:
            </h3>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              {/* Onboarding Call, EHR Integration, First Simulations items */}
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <Calendar
                  style={{
                    width: "20px",
                    height: "20px",
                    color: colors.primary,
                    marginRight: "12px",
                    marginTop: "2px",
                  }}
                />
                <div>
                  <div style={{ fontWeight: "500", color: colors.title }}>
                    Onboarding Call
                  </div>
                  <div style={{ fontSize: "14px", color: colors.subtext }}>
                    Personalized setup with our clinical team
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <Users
                  style={{
                    width: "20px",
                    height: "20px",
                    color: colors.primary,
                    marginRight: "12px",
                    marginTop: "2px",
                  }}
                />
                <div>
                  <div style={{ fontWeight: "500", color: colors.title }}>
                    EHR Integration
                  </div>
                  <div style={{ fontSize: "14px", color: colors.subtext }}>
                    Seamless connection to your existing systems
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "flex-start" }}>
                <Star
                  style={{
                    width: "20px",
                    height: "20px",
                    color: colors.primary,
                    marginRight: "12px",
                    marginTop: "2px",
                  }}
                />
                <div>
                  <div style={{ fontWeight: "500", color: colors.title }}>
                    First Simulations
                  </div>
                  <div style={{ fontSize: "14px", color: colors.subtext }}>
                    Generate your first patient simulations
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{ display: "flex", justifyContent: "center", gap: "16px" }}
          >
            <button
              style={{
                ...styles.secondaryButton,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() => {
                trackButtonClick("Schedule Call Now", {
                  location: "signup_complete",
                  action: "schedule_onboarding",
                });
                mixpanel.track(
                  "Onboarding Action",
                  getEventProperties({
                    action_type: "schedule_call",
                    signup_complete: true,
                  }),
                );
              }}
            >
              <Phone style={{ width: "16px", height: "16px" }} />
              Schedule Call Now
            </button>
            <button
              style={{
                ...styles.primaryButton,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onClick={() => {
                trackButtonClick("Contact Support", {
                  location: "signup_complete",
                  action: "contact_support",
                });
                mixpanel.track(
                  "Onboarding Action",
                  getEventProperties({
                    action_type: "contact_support",
                    signup_complete: true,
                  }),
                );
              }}
            >
              <Mail style={{ width: "16px", height: "16px" }} />
              Contact Support
            </button>
          </div>
        </div>
      )}
    </div>
  </div>
);

// --- Main PalladinoReferral Component ---
const PalladinoReferral = ({ referrerCode = "direct" }) => {
  const [currentPage, setCurrentPage] = useState("qr-landing");
  const [signupStep, setSignupStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    practice: "",
    specialty: "Plastic Surgery", // Default value
    email: "",
    ehr_system: "",
  });
  const [sessionId] = useState(
    () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  );
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const pageLoadTime = useRef(Date.now());
  const demoStartTime = useRef(null);
  const signupStartTime = useRef(null);

  const [stats, setStats] = useState({
    // Kept here as it's top-level state for now
    surgeons: 1500,
    procedures: 12840,
    timeSaved: 9.8,
  });

  const handleToggleHamburger = useCallback(() => {
    setIsMobileMenuOpen((prev) => !prev);
  }, []);

  // --- Mixpanel Tracking Functions (Memoized) ---
  const getEventProperties = useCallback(
    (additionalProps = {}) => ({
      referrer_code: referrerCode,
      session_id: sessionId,
      device_type: window.innerWidth < 768 ? "mobile" : "desktop",
      current_page: currentPage, // currentPage is a dependency
      ...additionalProps,
    }),
    [referrerCode, sessionId, currentPage],
  );

  const trackPageView = useCallback(
    (page) => {
      const timeOnPreviousPage = pageLoadTime.current
        ? Math.round((Date.now() - pageLoadTime.current) / 1000)
        : 0;
      mixpanel.track(
        "Page Viewed",
        getEventProperties({
          page: page,
          previous_page: currentPage, // currentPage is a dependency
          time_on_previous_page: timeOnPreviousPage,
        }),
      );
      pageLoadTime.current = Date.now();
    },
    [getEventProperties, currentPage],
  ); // Added currentPage

  const trackButtonClick = useCallback(
    (buttonName, properties = {}) => {
      mixpanel.track(
        "Button Clicked",
        getEventProperties({
          button_name: buttonName,
          ...properties,
        }),
      );
    },
    [getEventProperties],
  );

  const trackFormField = useCallback(
    (fieldName, action) => {
      mixpanel.track(
        "Form Field Interaction",
        getEventProperties({
          field_name: fieldName,
          action: action,
          form_step: signupStep, // signupStep is a dependency
        }),
      );
    },
    [getEventProperties, signupStep],
  );

  const trackFunnelStep = useCallback(
    (step, properties = {}) => {
      mixpanel.track(
        "Funnel Step Completed",
        getEventProperties({
          funnel_name: "Surgeon Referral",
          step: step,
          step_number:
            [
              "view_landing",
              "view_demo",
              "start_signup",
              "complete_profile",
              "select_ehr",
              "finish_signup",
            ].indexOf(step) + 1,
          ...properties,
        }),
      );
    },
    [getEventProperties],
  );

  // --- Form Input Handlers (Memoized) ---
  const handleNameChange = useCallback(
    (e) => {
      setFormData((prev) => ({ ...prev, name: e.target.value }));
      if (e.target.value.length === 1) {
        trackFormField("name", "started_typing");
      }
    },
    [trackFormField],
  ); // setFormData is stable

  const handleNameFocus = useCallback(() => {
    trackFormField("name", "focused");
  }, [trackFormField]);

  const handleNameBlur = useCallback(() => {
    trackFormField("name", "blurred");
  }, [trackFormField]);

  const handlePracticeChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, practice: e.target.value }));
    // No specific tracking on first char like name, add if needed
  }, []); // setFormData is stable

  const handlePracticeFocus = useCallback(() => {
    trackFormField("practice_name", "focused");
  }, [trackFormField]);

  const handlePracticeBlur = useCallback(() => {
    trackFormField("practice_name", "blurred");
  }, [trackFormField]);

  const handleEmailChange = useCallback((e) => {
    setFormData((prev) => ({ ...prev, email: e.target.value }));
  }, []); // setFormData is stable

  const handleEmailFocus = useCallback(() => {
    trackFormField("email", "focused");
  }, [trackFormField]);

  const handleEmailBlur = useCallback(() => {
    trackFormField("email", "blurred");
  }, [trackFormField]);

  // --- Effects ---
  useEffect(() => {
    trackPageView("qr-landing"); // Initial page view
    mixpanel.track(
      "Referral Session Started",
      getEventProperties({
        landing_url: window.location.href,
        timestamp: new Date().toISOString(),
        entry_page: "qr-landing",
        user_agent: navigator.userAgent,
      }),
    );

    const handleUnload = () => {
      const timeSpent = Math.round((Date.now() - pageLoadTime.current) / 1000);
      mixpanel.track(
        "Referral Session Ended",
        getEventProperties({
          time_spent_seconds: timeSpent,
          last_page: currentPage,
          completed_signup: currentPage === "signup" && signupStep === 3,
        }),
      );
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [referrerCode, sessionId, getEventProperties, currentPage, signupStep]); // Added currentPage and signupStep due to their use in getEventProperties for handleUnload

  useEffect(() => {
    trackPageView(currentPage);
    if (currentPage !== "qr-landing") {
      setIsVideoPlaying(false);
    }
  }, [currentPage, trackPageView]); // trackPageView is memoized

  useEffect(() => {
    let maxScroll = 0;
    const handleScroll = () => {
      if (typeof window !== "undefined" && typeof document !== "undefined") {
        const scrollPercentage = Math.round(
          (window.scrollY /
            (document.documentElement.scrollHeight - window.innerHeight)) *
            100,
        );
        if (scrollPercentage > maxScroll) {
          maxScroll = scrollPercentage;
          if (maxScroll % 25 === 0 && maxScroll > 0 && maxScroll <= 100) {
            // check maxScroll > 0 and <= 100
            mixpanel.track(
              "Scroll Depth Reached",
              getEventProperties({
                depth_percentage: maxScroll,
              }),
            );
          }
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [getEventProperties, currentPage]); // currentPage for context if scroll tracking is page-specific

  useEffect(() => {
    const styleTag = document.createElement("style");
    styleTag.textContent = `
      @keyframes pulse {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
      /* Global input focus style can be defined here or in a CSS file */
      /* input:focus {
           border-color: ${colors.primary} !important; 
           outline: none !important;
         } 
      */
    `;
    document.head.appendChild(styleTag);
    return () => {
      document.head.removeChild(styleTag);
    };
  }, []); // Empty dependency array if colors.primary is stable (which it is)

  // --- Render Current Page ---
  const renderCurrentPage = () => {
    const pageProps = {
      // Consolidate common props
      trackButtonClick,
      trackFunnelStep,
      setCurrentPage,
      getEventProperties,
      handleToggleHamburger,
      isMobileMenuOpen,
      // Specific props for each page will be spread below
    };

    switch (currentPage) {
      case "qr-landing":
        return (
          <QRLandingPageComponent
            {...pageProps}
            stats={stats}
            isVideoPlaying={isVideoPlaying}
            setIsVideoPlaying={setIsVideoPlaying}
            demoStartTime={demoStartTime}
            referrerCode={referrerCode} // Pass if needed directly by QRLandingPage
          />
        );
      case "demo":
        return (
          <DemoPageComponent
            {...pageProps}
            signupStartTime={signupStartTime}
            demoStartTime={demoStartTime}
          />
        );
      case "signup":
        return (
          <SignupPageComponent
            {...pageProps}
            signupStep={signupStep}
            setSignupStep={setSignupStep}
            formData={formData}
            setFormData={setFormData} // Pass the main setter
            handleNameChange={handleNameChange} // Or pass individual handlers
            handleNameFocus={handleNameFocus}
            handleNameBlur={handleNameBlur}
            handlePracticeChange={handlePracticeChange}
            handlePracticeFocus={handlePracticeFocus}
            handlePracticeBlur={handlePracticeBlur}
            handleEmailChange={handleEmailChange}
            handleEmailFocus={handleEmailFocus}
            handleEmailBlur={handleEmailBlur}
            trackFormField={trackFormField}
            signupStartTime={signupStartTime}
          />
        );
      default:
        return (
          <QRLandingPageComponent
            {...pageProps}
            stats={stats}
            isVideoPlaying={isVideoPlaying}
            setIsVideoPlaying={setIsVideoPlaying}
            demoStartTime={demoStartTime}
            referrerCode={referrerCode}
          />
        );
    }
  };

  return (
    <div style={{ minHeight: "100vh", overflowY: "auto" }}>
      {renderCurrentPage()}
    </div>
  );
};

export default PalladinoReferral;
